import * as React from 'react'
import {ChangeEvent, ReactElement} from 'react'
import {AsyncCommunicationProps} from '../../../../components/shared/hoc/withAsyncCommunication'
import EventEmitterExResident from '../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    CondominiumInfo,
    NatPostCondominiumListByResidentResponse
} from '../../condominium/repository/NatPostCondominiumListByResident'
import ResponsiveDialog from '../../../../components/shared/ResponsiveDialog'
import {currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import styled from 'styled-components'
import {PrimaryStyledButton, StyledSecondaryButton, UnstyledLinkStyledButton} from '../../../../styledComponents/Button'
import {Colors, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {
    ColumnFlexContainer,
    FlexContainer,
    FlexGrowContent,
    FullWidthColumnFlexContainer,
    RowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import {Close} from '@mui/icons-material'
import {Messages} from '../../../../Messages'
import {Box} from '@mui/material';
import NatResidentApplicationDetailFormItem
    from '../../applications/common/resident/NatResidentApplicationDetailFormItem'
import {PPTextFieldForResident} from '../../../../components/shared/forms/resident/PPTextFieldForResident'

interface Props {
    condominiumRepo: NatCondominiumRepo
    isDialogOpen: boolean
    onClose: () => void
    onClickCondominiumSelect: (
        condominiumCd: string, condominiumName: string
    ) => void
}

interface State {
    zipCode: string
    condominiumList: Nullable<CondominiumInfo[]>
}

export class NatResidentCondominiumSelectDialog
    extends React.Component<Props & AsyncCommunicationProps & { eventEmitter: EventEmitterExResident }, State> {

    constructor(props: Props & AsyncCommunicationProps & { eventEmitter: EventEmitterExResident }) {
        super(props)

        this.state = {
            zipCode: '',
            condominiumList: null
        }
        this.handleChangeZipCode = this.handleChangeZipCode.bind(this)
        this.onClickCondominiumSearch = this.onClickCondominiumSearch.bind(this)
        this.onClose = this.onClose.bind(this)
    }

    handleChangeZipCode(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({zipCode: event.target.value})
    }

    async onClickCondominiumSearch(): Promise<void> {
        const {condominiumRepo} = this.props
        const {zipCode} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPostCondominiumListByResidentResponse> = await this.props.asyncCallFunction(
                condominiumRepo.getCondominiumListByResident.bind(
                    condominiumRepo, zipCode),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    await this.props.eventEmitter.emit('clearHeadingAreaMessage')
                    this.setState({condominiumList: null})
                },
            )

            if (response && response.requestError === null) {
                this.setState({condominiumList: response.details.condominiumInfoList})
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    onClose(): void {
        this.props.onClose()
    }

    onClickCondominiumSelect(condominiumCd: string, condominiumName: string): void {
        this.props.onClickCondominiumSelect(condominiumCd, condominiumName)
    }

    renderCondominiumSearch(): ReactElement {
        const {zipCode} = this.state

        return (<>
                <NatResidentApplicationDetailFormItem
                    title={<span>郵便番号<BreakPcOnly/>（ハイフン不要）</span>}
                    component={
                        <PPTextFieldForResident
                            inputProps={{
                                name: 'zipCode',
                                maxLength: 7,
                                value: zipCode,
                                placeholder: '例：1234567'
                            }}
                            onChange={this.handleChangeZipCode}
                            eventKeys={['zipCode']}
                            eventEmitter={this.props.eventEmitter}
                        />
                    }
                />
                <CondominiumSearchButton onClick={this.onClickCondominiumSearch}>
                    <FullWidthColumnFlexContainer>検索</FullWidthColumnFlexContainer>
                </CondominiumSearchButton>
            </>)
    }

    renderCondominiumList(): ReactElement {
        const {condominiumList} = this.state

        if (!condominiumList) {
            return <></>
        }
        if (condominiumList.length === 0) {
            return <>{Messages.IOPCC0014.toString('')}</>
        }

        return (
            <Box>
                <CondominiumListContainer>
                    {condominiumList.map((condominium, index) => (
                        <CondominiumContainer key={index}>
                            <div>{condominium.condominiumName}</div>
                            <Box flexShrink={0}>
                                <CondominiumSelectButton
                                    onClick={() => this.onClickCondominiumSelect(
                                        condominium.condominiumCd,
                                        condominium.condominiumName)}>
                                    選択
                                </CondominiumSelectButton>
                            </Box>
                        </CondominiumContainer>
                    ))}
                </CondominiumListContainer>
            </Box>
        )
    }

    render(): ReactElement {
        const {zipCode} = this.state

        return (
            <ResponsiveDialog
                id={'pp-dialog'}
                open={this.props.isDialogOpen}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                fullWidth
            >
                <PanelArea>
                    <DialogTitleArea>
                        <RowFlexContainer>
                            <DialogTitle>マンション検索</DialogTitle>
                            <DialogCloseButton onClick={this.onClose} id={'close'}>
                                <Close fontSize={'large'} style={{color: `${Colors.AccentDark}`}}/>
                            </DialogCloseButton>
                        </RowFlexContainer>
                    </DialogTitleArea>
                    <DisplayItemDividerDialog/>
                    <MainArea>
                        <Box height={'15px'}/>
                        {this.renderCondominiumSearch()}
                        {this.renderCondominiumList()}
                    </MainArea>
                    <FlexGrowContent/>
                </PanelArea>
            </ResponsiveDialog>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
const DialogCloseButton = styled(UnstyledLinkStyledButton)`
  height: 44px;
  width: 44px;
  color: ${Colors.AccentDark};
`
const DisplayItemDividerDialog = styled.hr`
  border: none;
  padding: 0;
  margin: 0;
  border-bottom: 1px solid ${Colors.AccentLight};
`
const DialogTitleArea = styled.div`
  margin: 10px;
`
const PanelArea = styled(ColumnFlexContainer)`
  height: 100%;
  align-items: stretch;
  padding-bottom: 30px;
  overflow-wrap: anywhere;
`
const MainArea = styled.div`
  margin: 0 15px;
`
const DialogTitle = styled.div`
  font-size: ${FontSize.Accent};
  font-weight: ${FontWeights.Bold};
  color: ${Colors.AccentDark};
  margin-left: 5px;
`
const CondominiumSearchButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin: 15px auto 15px;
`
const CondominiumListContainer = styled(ColumnFlexContainer)`
  border: 1px solid ${Colors.AccentLight};
  color: ${Colors.AccentDark};
  height: 100%;
  max-height: 420px;
  overflow-y: auto;
`
const CondominiumContainer = styled(FlexContainer)`
  border-bottom: 1px solid ${Colors.AccentLight};
  width: 100%;
  padding: 10px;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`
const CondominiumSelectButton = styled(PrimaryStyledButton)`
  width: auto;
  margin: 0;
  padding: 10px 20px;
  font-weight: 400;
`
